import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default marker icons when using Leaflet directly
// This avoids missing icon issues in many bundlers
// Note: safe to run multiple times
// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

export type LatLng = { lat: number; lng: number };

interface EventLocationPickerProps {
  position: LatLng | null;
  onChange: (lat: number, lng: number) => void;
  radius?: number; // optional check-in radius to preview
}

const DEFAULT_CENTER: LatLng = { lat: 41.9028, lng: 12.4964 }; // Rome

const EventLocationPicker: React.FC<EventLocationPickerProps> = ({ position, onChange, radius }) => {
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const circleRef = useRef<L.Circle | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, {
      center: [position?.lat ?? DEFAULT_CENTER.lat, position?.lng ?? DEFAULT_CENTER.lng],
      zoom: 13,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);

    const ensureMarker = (lat: number, lng: number) => {
      if (!markerRef.current) {
        markerRef.current = L.marker([lat, lng], { draggable: true }).addTo(map);
        markerRef.current.on('dragend', () => {
          const pos = markerRef.current!.getLatLng();
          onChange(pos.lat, pos.lng);
        });
      } else {
        markerRef.current.setLatLng([lat, lng]);
      }
    };

    if (position) {
      ensureMarker(position.lat, position.lng);
    }

    if (radius && position) {
      circleRef.current = L.circle([position.lat, position.lng], { radius, color: '#22c55e', fillOpacity: 0.1 }).addTo(map);
    }

    map.on('click', (e: L.LeafletMouseEvent) => {
      const { lat, lng } = e.latlng;
      ensureMarker(lat, lng);
      map.panTo([lat, lng]);
      if (circleRef.current) circleRef.current.setLatLng([lat, lng]);
      onChange(lat, lng);
    });

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
      circleRef.current = null;
    };
  }, []);

  // Sync position updates from parent
  useEffect(() => {
    if (!mapRef.current || !position) return;
    mapRef.current.flyTo([position.lat, position.lng], mapRef.current.getZoom());
    if (!markerRef.current) {
      markerRef.current = L.marker([position.lat, position.lng], { draggable: true }).addTo(mapRef.current);
      markerRef.current.on('dragend', () => {
        const pos = markerRef.current!.getLatLng();
        onChange(pos.lat, pos.lng);
      });
    } else {
      markerRef.current.setLatLng([position.lat, position.lng]);
    }

    if (radius) {
      if (!circleRef.current) {
        circleRef.current = L.circle([position.lat, position.lng], { radius, color: '#22c55e', fillOpacity: 0.1 }).addTo(mapRef.current);
      } else {
        circleRef.current.setLatLng([position.lat, position.lng]);
        circleRef.current.setRadius(radius);
      }
    } else if (circleRef.current) {
      circleRef.current.remove();
      circleRef.current = null;
    }
  }, [position?.lat, position?.lng, radius]);

  return <div ref={containerRef} className="h-full w-full" />;
};

export default EventLocationPicker;
